// src/screens/CartScreen.js
// ============================================================================
// CART SCREEN
//
// Renders the user's local merch cart (from CartContext): thumbnail / name /
// size / qty stepper / line total / remove button per line, plus a subtotal
// and a "Checkout" CTA that routes to MerchCheckoutScreen with the cart in
// route params. Empty-state when the cart has no items.
//
// Aesthetic mirrors FounderStoreScreen + MerchCheckoutScreen — dark cards,
// gold accents, same Header / Footer / NavBar chrome. Web-first, but RN
// primitives only so it renders on native too.
// ============================================================================

import React from 'react';
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import Header from '../components/Header';
import Footer from '../components/Footer';
import NavBar from '../components/NavBar';
import { useCart } from '../context/CartContext';

const usd = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

export default function CartScreen({ navigation }) {
  const { items, itemCount, subtotalCents, setQty, removeItem, clear } = useCart();

  const handleCheckout = () => {
    if (!items.length) return;
    // Pass the cart as route params. MerchCheckoutScreen detects the cart
    // array and switches to multi-item rendering / submission.
    navigation.navigate('MerchCheckout', {
      cart: items.map((l) => ({
        productKey: l.productKey,
        size: l.size,
        sizeLabel: l.sizeLabel,
        productName: l.productName,
        unitAmountCents: l.unitAmountCents,
        quantity: l.quantity,
      })),
    });
  };

  return (
    <SafeAreaView style={styles.container}>
      <Header navigation={navigation} />
      <ScrollView contentContainerStyle={styles.scrollContent}>
        <View style={styles.headerRow}>
          <View style={{ flex: 1 }}>
            <Text style={styles.eyebrow}>YOUR CART</Text>
            <Text style={styles.title}>
              {itemCount === 0 ? 'Cart is empty' : `${itemCount} ${itemCount === 1 ? 'item' : 'items'}`}
            </Text>
          </View>
          {items.length > 0 ? (
            <TouchableOpacity onPress={clear} style={styles.clearBtn}>
              <Ionicons name="trash-outline" size={16} color="#999" />
              <Text style={styles.clearBtnText}>Clear cart</Text>
            </TouchableOpacity>
          ) : null}
        </View>

        {items.length === 0 ? (
          <View style={styles.emptyState}>
            <Ionicons name="bag-outline" size={64} color="#333" />
            <Text style={styles.emptyTitle}>Nothing in here yet</Text>
            <Text style={styles.emptyBody}>
              Browse the store and add gear to your cart. Everything you add
              stays put — even if you close the tab.
            </Text>
            <TouchableOpacity
              style={styles.primaryBtn}
              onPress={() => navigation.navigate('Store')}
            >
              <Text style={styles.primaryBtnText}>Browse Store</Text>
            </TouchableOpacity>
          </View>
        ) : (
          <>
            {/* Line items */}
            {items.map((line) => {
              const lineTotal = (line.unitAmountCents || 0) * (line.quantity || 0);
              return (
                <View key={`${line.productKey}_${line.size}`} style={styles.lineCard}>
                  {/* Thumbnail (optional) */}
                  {line.image ? (
                    <Image source={line.image} style={styles.thumb} resizeMode="contain" />
                  ) : (
                    <View style={[styles.thumb, styles.thumbPlaceholder]}>
                      <Ionicons name="cube-outline" size={28} color="#444" />
                    </View>
                  )}

                  {/* Name + size + line total */}
                  <View style={styles.lineMain}>
                    <Text style={styles.lineName} numberOfLines={2}>{line.productName}</Text>
                    <Text style={styles.lineSize}>Size: {line.sizeLabel}</Text>
                    <Text style={styles.lineUnit}>{usd(line.unitAmountCents)} each</Text>

                    {/* Qty stepper */}
                    <View style={styles.qtyRow}>
                      <TouchableOpacity
                        style={styles.qtyBtn}
                        onPress={() => setQty(line.productKey, line.size, (line.quantity || 0) - 1)}
                      >
                        <Ionicons name="remove" size={18} color="#FCD000" />
                      </TouchableOpacity>
                      <Text style={styles.qtyValue}>{line.quantity}</Text>
                      <TouchableOpacity
                        style={styles.qtyBtn}
                        onPress={() => setQty(line.productKey, line.size, (line.quantity || 0) + 1)}
                      >
                        <Ionicons name="add" size={18} color="#FCD000" />
                      </TouchableOpacity>

                      <TouchableOpacity
                        style={styles.removeBtn}
                        onPress={() => removeItem(line.productKey, line.size)}
                      >
                        <Ionicons name="close" size={14} color="#999" />
                        <Text style={styles.removeBtnText}>Remove</Text>
                      </TouchableOpacity>
                    </View>
                  </View>

                  <Text style={styles.lineTotal}>{usd(lineTotal)}</Text>
                </View>
              );
            })}

            {/* Subtotal + checkout */}
            <View style={styles.totalCard}>
              <View style={styles.totalRow}>
                <Text style={styles.totalLabel}>Subtotal</Text>
                <Text style={styles.totalValue}>{usd(subtotalCents)}</Text>
              </View>
              <Text style={styles.shippingNote}>
                Shipping and tax (if any) calculated at checkout.
              </Text>
              <TouchableOpacity
                style={styles.primaryBtn}
                onPress={handleCheckout}
              >
                <Text style={styles.primaryBtnText}>
                  Checkout — {usd(subtotalCents)}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.secondaryBtn}
                onPress={() => navigation.navigate('Store')}
              >
                <Text style={styles.secondaryBtnText}>Keep Shopping</Text>
              </TouchableOpacity>
            </View>
          </>
        )}

        <View style={{ height: 100 }} />
      </ScrollView>
      <Footer navigation={navigation} />
      <NavBar navigation={navigation} />
    </SafeAreaView>
  );
}

// ============================================================================
// STYLES
// ============================================================================
const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#0A0A0A' },
  scrollContent: { paddingHorizontal: 20, paddingTop: 20 },

  headerRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    marginBottom: 18,
  },
  eyebrow: {
    color: '#00B4D8',
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 2,
    marginBottom: 4,
  },
  title: {
    color: '#FFF',
    fontSize: 26,
    fontWeight: '800',
  },
  clearBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#2a2a2a',
  },
  clearBtnText: { color: '#999', fontSize: 12, fontWeight: '600' },

  emptyState: {
    alignItems: 'center',
    paddingVertical: 60,
    paddingHorizontal: 16,
  },
  emptyTitle: { color: '#FFF', fontSize: 20, fontWeight: '700', marginTop: 16 },
  emptyBody: {
    color: '#888',
    fontSize: 14,
    lineHeight: 22,
    textAlign: 'center',
    marginTop: 8,
    marginBottom: 24,
    maxWidth: 360,
  },

  lineCard: {
    flexDirection: 'row',
    backgroundColor: '#141414',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#262626',
    padding: 12,
    marginBottom: 12,
    alignItems: 'flex-start',
    gap: 12,
  },
  thumb: {
    width: 72,
    height: 72,
    borderRadius: 8,
    backgroundColor: '#0E0E0E',
    borderWidth: 1,
    borderColor: '#2a2a2a',
  },
  thumbPlaceholder: { alignItems: 'center', justifyContent: 'center' },
  lineMain: { flex: 1, minWidth: 0 },
  lineName: { color: '#FFF', fontSize: 15, fontWeight: '700' },
  lineSize: { color: '#999', fontSize: 12, marginTop: 2 },
  lineUnit: { color: '#888', fontSize: 12, marginTop: 2 },
  qtyRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 10,
    gap: 8,
    flexWrap: 'wrap',
  },
  qtyBtn: {
    width: 28,
    height: 28,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#FCD00060',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#1a1a0a',
  },
  qtyValue: {
    color: '#FFF',
    fontSize: 14,
    fontWeight: '800',
    minWidth: 24,
    textAlign: 'center',
  },
  removeBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 4,
    paddingHorizontal: 8,
    marginLeft: 4,
  },
  removeBtnText: { color: '#999', fontSize: 12 },
  lineTotal: {
    color: '#FCD000',
    fontSize: 15,
    fontWeight: '800',
    marginLeft: 6,
  },

  totalCard: {
    backgroundColor: '#141414',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#262626',
    padding: 16,
    marginTop: 6,
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    marginBottom: 6,
  },
  totalLabel: { color: '#FFF', fontSize: 16, fontWeight: '700' },
  totalValue: { color: '#FCD000', fontSize: 22, fontWeight: '900' },
  shippingNote: { color: '#666', fontSize: 11, fontStyle: 'italic', marginBottom: 14 },

  primaryBtn: {
    backgroundColor: '#FCD000',
    paddingVertical: 14,
    borderRadius: 10,
    alignItems: 'center',
    marginBottom: 10,
    ...(Platform.OS === 'web' ? { boxShadow: '0 0 24px rgba(252, 208, 0, 0.30)' } : {}),
  },
  primaryBtnText: { color: '#0A0A0A', fontSize: 16, fontWeight: '800' },
  secondaryBtn: {
    paddingVertical: 12,
    borderRadius: 10,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#2a2a2a',
  },
  secondaryBtnText: { color: '#CCC', fontSize: 14, fontWeight: '700' },
});
